import {
  ActionsMenu,
  PageContentLayout,
  PageHeaderLayout,
  Loader,
  NothingFoundAlert,
  SideMenu,
  Typography,
} from 'melp-design/components';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'config';
import {
  BenefitBrandsList,
  BenefitContent,
  BenefitEmployeesAssignments,
  BenefitEmployeesGroupsAssignments,
  BenefitExpirationWarning,
  BenefitsDisabledFeatureAlert,
  BenefitSettings,
  CopyBenefitModal,
} from 'containers/benefits';
import { tabsSchema } from 'utils/schemas';
import { useMe } from 'state/Administrators';
import { useDialog } from 'utils/Dialog';
import { useTabsState } from 'utils/Tabs';
import { useRouteParams } from 'utils/useIdParam';
import { usePredefinedToasts } from 'utils/Toast';
import { useConfirm } from 'store/confirm';
import {
  COLOR_BY_BENEFIT_STATUS,
  useBenefit,
  useBenefitContent,
  useDeleteBenefit,
} from 'store/benefits';
import { useModalContext } from 'store/modal';
import { LanguageSelectModal, ShareModal } from 'components/modals';
import { generateDynamicLink } from 'utils/general';
import { useGuidedModeParam } from 'utils/useGuidedModeParam';
import { useBenefitsPDFDownload } from 'components/benefits/BenefitsPDF/hooks/useMultipleBenefitsPDFDownload';
import BenefitInfoPanel from '../../BenefitsOld/BenefitDetails/common/BenefitInfoPanel';
import Marketplace from '../../BenefitsOld/BenefitDetails/marketplace/Marketplace';
import BenefitWizardCompletionSuccessDialog from '../../BenefitsOld/BenefitDetails/common/BenefitWizardCompletionSuccessDialog';

const benefitDetailsTabsSchema = tabsSchema([
  'settings',
  'content',
  'marketplace',
  'brands',
  'employeesGroupsAssignment',
  'employeesAssignment',
]);

export const BenefitDetails = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const predefinedToasts = usePredefinedToasts();
  const { id: benefitId } = useRouteParams(ROUTES.benefits.details);
  const { confirmation } = useConfirm();
  const {
    canEditBenefits,
    isFeatureEnabled,
    hasEditPermission,
    hasViewPermission,
  } = useMe();
  const { openModal } = useModalContext();
  const { value: guided } = useGuidedModeParam();

  const sideTabs = useTabsState(
    [
      {
        key: 'settings',
        label: t('common.settings'),
      },
      {
        key: 'content',
        label: t('benefitTemplates.templateContent'),
      },
      isFeatureEnabled('marketplaceV2')
        ? {
            key: 'brands',
            label: t('sidebar.marketplace'),
          }
        : {
            key: 'marketplace',
            label: t('sidebar.marketplace'),
          },
      ...(hasViewPermission(['groups'])
        ? [
            {
              key: 'employeesGroupsAssignment',
              label: t('sidebar.assignGroups'),
            } as const,
          ]
        : []),
      ...(hasViewPermission(['employees'])
        ? [
            {
              key: 'employeesAssignment',
              label: t('sidebar.assignEmployees'),
            } as const,
          ]
        : []),
    ],
    benefitDetailsTabsSchema,
  );

  const { data: benefit, isLoading } = useBenefit(benefitId);
  const { data: content, isLoading: isContentLoading } =
    useBenefitContent(benefitId);

  const { mutate: deleteBenefit, isLoading: isDeleting } = useDeleteBenefit();
  const { downloadPdf, loading: isDownloading } = useBenefitsPDFDownload();

  const completionSuccessDialog = useDialog();

  if (isLoading) return <Loader />;
  if (!benefit) return <NothingFoundAlert />;

  return (
    <>
      <Stack gap={2}>
        <BenefitsDisabledFeatureAlert />

        <BenefitExpirationWarning benefitId={benefitId} />

        <Stack gap={2}>
          <PageHeaderLayout
            breadcrumbs={[
              {
                label: t('menu.benefits'),
                url: ROUTES.benefits.list,
              },
              {
                label: benefit.name,
                url: ROUTES.benefits.details.replace(':id', benefitId),
              },
            ]}
            side={
              <ActionsMenu
                items={[
                  {
                    label: t('common.downloadPDF'),
                    onClick: async () => {
                      const response = await openModal({
                        component: LanguageSelectModal,
                        props: {
                          languages:
                            content?.translations.map((t) => t.language) ?? [],
                        },
                      });

                      if (response.action === 'CONFIRM') {
                        downloadPdf([benefitId], response.language);
                      }
                    },
                    disabled: isDownloading || isContentLoading,
                  },
                  ...(benefit.status === 'draft'
                    ? []
                    : [
                        {
                          label: t('global.actions.share'),
                          onClick: () => {
                            openModal({
                              component: ShareModal,
                              props: {
                                url: generateDynamicLink({
                                  path: 'benefit',
                                  params: { benefitId },
                                }),
                              },
                            });
                          },
                        },
                      ]),
                  {
                    label: t('actions.make_copy'),
                    disabled:
                      !canEditBenefits || !hasEditPermission(['benefits']),
                    onClick: () => {
                      openModal({
                        component: CopyBenefitModal,
                        props: {
                          benefitId,
                          name: benefit.name,
                        },
                      });
                    },
                  },
                  {
                    label: t('actions.delete'),
                    onClick: async () => {
                      const { confirmed } = await confirmation(
                        t('benefits.deleteWarning'),
                        undefined,
                        'danger',
                      );

                      if (confirmed) {
                        deleteBenefit(
                          { benefitId },
                          {
                            onSuccess: () => {
                              predefinedToasts.success.deleted();
                              history.push(ROUTES.benefits.list);
                            },
                          },
                        );
                      }
                    },
                    color: 'error' as const,
                    disabled:
                      isDeleting ||
                      !canEditBenefits ||
                      !hasEditPermission(['benefits']),
                  },
                ]}
              />
            }
            under={
              <Typography color={COLOR_BY_BENEFIT_STATUS[benefit.status]}>
                {t(`status.${benefit.status}`)}
              </Typography>
            }
          />

          <PageContentLayout
            sidebar={
              <Stack gap="10px">
                <BenefitInfoPanel />
                <SideMenu
                  activeTab={sideTabs.activeTab}
                  onTabChange={sideTabs.goTo}
                  tabs={sideTabs.tabs}
                />
              </Stack>
            }
          >
            {sideTabs.activeTab === 'settings' && (
              <BenefitSettings
                onSaveSuccess={() => {
                  if (guided) sideTabs.next();
                }}
                guided={guided}
              />
            )}
            {sideTabs.activeTab === 'content' && (
              <BenefitContent
                onSaveSuccess={() => {
                  if (guided) {
                    if (benefit.isBalanceTrackingEnabled) {
                      sideTabs.next();
                    } else if (hasViewPermission(['groups'])) {
                      sideTabs.goTo('employeesGroupsAssignment');
                    } else if (hasViewPermission(['employees'])) {
                      sideTabs.goTo('employeesAssignment');
                    } else {
                      completionSuccessDialog.openDialog();
                    }
                  }
                }}
                guided={guided}
              />
            )}
            {sideTabs.activeTab === 'marketplace' && (
              <Marketplace
                onSaveSuccess={() => {
                  if (guided) sideTabs.next();
                }}
                guided={guided}
              />
            )}
            {sideTabs.activeTab === 'employeesGroupsAssignment' &&
              hasViewPermission(['groups']) && (
                <BenefitEmployeesGroupsAssignments
                  onSaveSuccess={() => {
                    if (guided) sideTabs.next();
                  }}
                  guided={guided}
                />
              )}
            {sideTabs.activeTab === 'employeesAssignment' &&
              hasViewPermission(['employees']) && (
                <BenefitEmployeesAssignments
                  onSaveSuccess={() => {
                    if (guided) {
                      completionSuccessDialog.openDialog();
                    }
                  }}
                  guided={guided}
                />
              )}

            {sideTabs.activeTab === 'brands' && <BenefitBrandsList />}
          </PageContentLayout>
        </Stack>
      </Stack>

      <BenefitWizardCompletionSuccessDialog
        benefitId={benefitId}
        open={completionSuccessDialog.open}
        onClose={completionSuccessDialog.closeDialog}
      />
    </>
  );
};
